// InstructorPage.jsx
import React, { useEffect, useState } from 'react';
import { BookOpen, LifeBuoy, LogOut, MessageSquarePlus, MessageSquare, Users, Video } from 'lucide-react';
import { Button } from './ui-common/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui-common/card';
import Spinner from './ui-common/spinner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui-common/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui-common/select';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, CartesianGrid, ResponsiveContainer
} from 'recharts';
import axiosInstance from './axiosInstance';

function ReferenceClicksBarChart({ data }) {
  // Map technical activity types to user-friendly labels
  const labelMapping = {
    'submit_question': 'Questions',
    'link_click': 'References'
  };

  const chartData = Array.isArray(data)
    ? data.map((item) => ({
        name: labelMapping[item.activity_type] || item.activity_type,
        count: Number(item.count),
      }))
    : [];

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis allowDecimals={false} />
        <RechartsTooltip />
        <Bar dataKey="count" fill="#82ca9d" />
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function InstructorPage({ user, onBack, onLogout, onNavigateToSupport }) {
  // --- Analytics dashboard state ---
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  // New analytics-based metrics state
  const [questions, setQuestions] = useState(null);
  const [referenceClicks, setReferenceClicks] = useState([]);
  const [materialStats, setMaterialStats] = useState(null);
  const [avgQuestionsPerStudent, setAvgQuestionsPerStudent] = useState(null);

  // Load courses once
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoadingCourses(true);
        const { data } = await axiosInstance.get('/api/instructor_courses');
        const courseList = (Array.isArray(data) ? data : []).filter(Boolean);
        setCourses(courseList);
        setSelectedCourse(prev => prev ?? (courseList.length > 0 ? courseList[0].name : null));
      } catch (e) {
        console.error('Failed to fetch courses for instructor:', e);
      } finally {
        setLoadingCourses(false);
      }
    };
    fetchCourses();
  }, []);

  // Fetch new analytics metrics when selectedCourse changes
  useEffect(() => {
    if (!selectedCourse) return;
    let ignore = false;
    const course = encodeURIComponent(selectedCourse);  // internal name for auth + Athena resolution
    const fetchAllAnalytics = async () => {
      try {
        setLoadingMetrics(true);
        const [
          questionsRes,
          referenceClicksRes,
          materialStatsRes,
          avgQuestionsPerStudentRes,
        ] = await Promise.all([
          axiosInstance.get(`/api/courses/questions/total/${course}`),
          axiosInstance.get(`/api/courses/references/clicks/${course}`),
          axiosInstance.get(`/api/courses/material/stats/${course}`),
          axiosInstance.get(`/api/courses/questions/avg-per-student/${course}`)
        ]);
        if (ignore) return;
        setQuestions(questionsRes?.data ?? null);
        setReferenceClicks(Array.isArray(referenceClicksRes?.data) ? referenceClicksRes.data : []);
        setMaterialStats(
          Array.isArray(materialStatsRes?.data)
            ? materialStatsRes.data[0] ?? null
            : materialStatsRes?.data ?? null
        );
        setAvgQuestionsPerStudent(
          typeof avgQuestionsPerStudentRes?.data === "object"
            ? avgQuestionsPerStudentRes.data.avg_questions_per_student ?? 0
            : Number(avgQuestionsPerStudentRes?.data ?? 0)
        );
      } catch (e) {
        if (!ignore) {
          console.error('Failed loading new analytics metrics:', e);
          setQuestions(null);
          setReferenceClicks([]);
          setMaterialStats(null);
          setAvgQuestionsPerStudent(null);
        }
      } finally {
        if (!ignore) setLoadingMetrics(false);
      }
    };
    fetchAllAnalytics();
    return () => { ignore = true; };
  }, [selectedCourse]);

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
        {/* Header (identical layout to CoursesPage) */}
        <div className="sticky top-0 z-10 border-b backdrop-blur-sm bg-muted/95">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Instructor Dashboard</h1>
                <p className="text-muted-foreground mt-1">
                  Welcome back, {user?.name || 'Instructor'}!
                </p>
              </div>
              <div className="flex gap-1 items-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                      <Button variant="outline" size="default" className="gap-2" onClick={onBack}>
                      <BookOpen className="h-4 w-4" />
                      Courses
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Back to Courses</p>
                  </TooltipContent>
                </Tooltip>
                <div className="flex gap-0.5 items-center">
                  {/* <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" size="icon">
                        <BookText className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>User Manual</p>
                    </TooltipContent>
                  </Tooltip> */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" size="icon" onClick={onNavigateToSupport}>
                        <LifeBuoy className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Support & Feedback</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon" onClick={onLogout}>
                      <LogOut className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Logout</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </div>
          {(loadingCourses || loadingMetrics) && (
            <div className="h-1 w-full bg-primary/30 animate-pulse" />
          )}
        </div>

        {/* --- Analytics Dashboard --- */}
        <div className="max-w-6xl mx-auto p-6 space-y-6">
          {/* Course Selector (single dropdown above analytics cards/charts) */}
          <div className="mb-6 w-full">
            <h2 className="text-2xl font-bold mb-2">Select Course</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Choose a course to view its dashboard and analytics
            </p>
            {loadingCourses ? (
              <div className="animate-pulse space-y-2">
                <div className="h-10 w-full max-w-md bg-muted rounded" />
              </div>
            ) : (
              <Select value={selectedCourse} onValueChange={setSelectedCourse} disabled={courses.length === 0}>
                <SelectTrigger className="w-full max-w-md">
                  <SelectValue placeholder={courses[0]?.label || 'No courses available'} />
                </SelectTrigger>
                <SelectContent>
                  {courses.map((c) => (
                    <SelectItem key={c.name} value={c.name}>{c.label} - {c.id ? ` ${c.id}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {selectedCourse && (
            <>
              {/* Analytics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <MessageSquare className="h-4 w-4" />
                      Questions Submitted
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingMetrics ? (
                      <div className="h-20 flex items-center justify-center">
                        <Spinner size={32} thickness={4} className="border-foreground/30" />
                      </div>
                    ) : (
                      <div className="flex flex-col items-start">
                        <div className="text-3xl font-bold leading-none">
                          {questions !== null && typeof questions === 'object'
                            ? (questions.total_questions ?? questions.total ?? questions.count ?? 0)
                            : (questions ?? 0)}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          In Total
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Users className="h-4 w-4" />
                      Avg. Questions/Student
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingMetrics ? (
                      <div className="h-20 flex items-center justify-center">
                        <Spinner size={32} thickness={4} className="border-foreground/30" />
                      </div>
                    ) : (
                      <div className="flex flex-col items-start">
                        <div className="text-3xl font-bold leading-none">
                          {avgQuestionsPerStudent !== null && avgQuestionsPerStudent !== undefined
                            ? Number(avgQuestionsPerStudent).toFixed(2)
                            : '0.00'}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Per active student
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Video className="h-4 w-4" />
                      Learning Material Coverage
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingMetrics ? (
                      <div className="h-20 flex items-center justify-center">
                        <Spinner size={32} thickness={4} className="border-foreground/30" />
                      </div>
                    ) : (
                      (() => {
                        const stats = Array.isArray(materialStats)
                          ? materialStats[0]
                          : materialStats;

                        if (!stats) {
                          return <span className="text-muted-foreground">No data</span>;
                        }

                        const numLectures = Number(stats.num_lectures ?? 0);
                        const totalVideoHours = Number(stats.total_video_hours ?? 0);
                        const numPdfs = Number(stats.num_pdfs ?? 0);

                        return (
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="flex flex-col items-center">
                              <div className="flex items-center gap-1">
                                <Video className="h-4 w-4" />
                                <span className="text-2xl font-bold">{numLectures}</span>
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">Video Lectures</div>
                            </div>
                            <div className="flex flex-col items-center">
                              <div className="flex items-center gap-1">
                                <Video className="h-4 w-4" />
                                <span className="text-2xl font-bold">{totalVideoHours}</span>
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">Video Hours</div>
                            </div>
                            <div className="flex flex-col items-center">
                              <div className="flex items-center gap-1">
                                <BookOpen className="h-4 w-4" />
                                <span className="text-2xl font-bold">{numPdfs.toLocaleString()}</span>
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">PDF Docs</div>
                            </div>
                          </div>
                        );
                      })()
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Analytics Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2">
                      Top Q&A Resources
                    </CardTitle>
                    <CardDescription>
                      View most referenced course materials
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px] flex items-center justify-center">
                      <p className="text-muted-foreground">Coming Soon</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2">
                      Student Engagement: Questions & Materials
                    </CardTitle>
                    <CardDescription>
                      Number of views
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingMetrics ? (
                      <div className="h-48 flex items-center justify-center">
                        <Spinner size={32} thickness={4} className="border-foreground/30" />
                      </div>
                    ) : (
                      <ReferenceClicksBarChart data={referenceClicks} />
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Feedback Section - Invite instructors to suggest metrics */}
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <MessageSquarePlus className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-foreground mb-1">
                        Help us improve the dashboard
                      </h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        We're constantly working to provide better insights for instructors.
                        If you have ideas for additional metrics or analytics that would be valuable for your teaching,
                        we'd love to hear from you!
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={onNavigateToSupport}
                      >
                        <MessageSquarePlus className="h-4 w-4" />
                        Suggest a Metric
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </TooltipProvider>
  )
}